/**
 * The fixed set of SYSTEM ids. They may be re-labelled and re-expressed but never renamed or removed:
 * the estimator, the inventory dossier, the dashboard and every saved snapshot read these names.
 */
export const SYSTEM_EQUATION_IDS = [
    'age',
    'dep',
    'expectedKm',
    'mileage',
    'owner',
    'base',
    'inspection',
    'fixed',
    'inspected',
    'afterFace',
    'afterCustoms',
    'inclusion',
    'appraisal',
    'remaining',
    'licensing',
    'general',
    'registration',
    'transfer',
    'insurance',
    'commission',
    'fuel',
    'overhead',
    'holding',
    'repair',
    'taxTraffic',
    'otherDirect',
    'costs',
    'profitAllowance',
    'ceiling',
    'total',
    'retail',
    'profit',
    'newMarginFactor',
    'supportRate',
    'support',
];
export const isSystemEquationId = (value) => SYSTEM_EQUATION_IDS.includes(value);
/** A CUSTOM intermediate must look like `x_someName`, so it can never collide with a future SYSTEM id. */
export const CUSTOM_ID_PATTERN = /^x_[a-z][a-zA-Z0-9]*$/;
/**
 * The two expressions the 2026-09-23 business review changed (plan §11.19), exported with the ones they
 * replaced so migration `0008` and its spec can say exactly which text they rewrite: a workspace whose
 * administrator had already edited either equation keeps its own version.
 *
 *  - `dep` compounds instead of adding: value₁ = price × (1 − rate₁), value₂ = value₁ × (1 − rate₂) …
 *    so the combined fraction is 1 − Π(1 − rateₙ). `base` keeps its expression and its id; only the
 *    fraction it reads changed.
 *  - `ceiling` no longer deducts the expected costs after buying: the most CTA may pay is the appraisal
 *    less the profit it wants and the safety margin.
 */
export const SEED_DEP_EXPRESSION = '1 - (1 - if(age >= 1, depYear1, 0)) * (1 - if(age >= 2, depYear2, 0)) * (1 - if(age >= 3, depYear3, 0)) * (1 - if(age >= 4, depYear4, 0))';
export const SEED_CEILING_EXPRESSION = 'floor((appraisal - profitAllowance - riskBuffer) / roundingIncrement) * roundingIncrement';
/** What `dep` and `ceiling` said before the review, for the migration and its spec. */
export const PREVIOUS_SEED_EXPRESSIONS = {
    dep: 'if(age >= 1, depYear1, 0) + if(age >= 2, depYear2, 0) + if(age >= 3, depYear3, 0) + if(age >= 4, depYear4, 0)',
    ceiling: 'floor((appraisal - costs - profitAllowance - riskBuffer) / roundingIncrement) * roundingIncrement',
};
export const SEED_POLICY_V1 = [
    // ── Age & value ───────────────────────────────────────────────────────────────────────────────
    {
        id: 'age',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Vehicle age',
        unit: 'years',
        group: 'AGE_VALUE',
        expression: 'max(1, valuationYear - modelYear + 1)',
        description: 'PP §7.1 AgeBand. Model-year age is retained to reconcile the workbook; the valuation year comes from the saved valuation date, never from the day a record is reopened. A different age basis needs an explicit policy decision.',
    },
    {
        id: 'dep',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Age depreciation (compounded)',
        unit: 'fraction',
        group: 'AGE_VALUE',
        expression: SEED_DEP_EXPRESSION,
        description: "Year-by-year depreciation, compounded (business review 2026-09-23): each year's rate applies to the value left after the year before, for years 1 through min(age, 4). The combined fraction is 1 − Π(1 − rate). Depreciation after year four awaits a decision.",
    },
    {
        id: 'expectedKm',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Expected mileage',
        unit: 'km',
        group: 'AGE_VALUE',
        expression: 'age * annualMileageAllowance',
        description: 'PP §7.1 ExpectedKm: the mileage the vehicle would be expected to carry at this age.',
    },
    {
        id: 'mileage',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Mileage penalty',
        unit: 'fraction',
        group: 'AGE_VALUE',
        expression: 'ceil(max(0, odometer - expectedKm) / mileageStepKm) * mileageStepRate',
        description: 'PP §7.1 MileagePenalty: one started step of excess mileage costs one step rate. Mileage below the expectation carries no bonus.',
    },
    {
        id: 'owner',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Owner penalty',
        unit: 'fraction',
        group: 'AGE_VALUE',
        expression: 'max(0, ownerCount - ownerAllowance) * ownerStepRate',
        description: 'PP §7.1 OwnerPenalty: each owner above the allowance deducts one step rate.',
    },
    {
        id: 'base',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Value after age, mileage and owners',
        unit: 'JOD',
        group: 'AGE_VALUE',
        expression: 'referencePrice * (1 - dep) * (1 - mileage) * (1 - owner)',
        description: 'PP §7.1 BaseValue with compounded depreciation: referencePrice × Π(1 − depYearN) × (1 − mileage) × (1 − owner). At 21,200 JOD reference, model year 2025, valuation year 2026 and 42,000 km this is 16,136.91 JOD (it was 15,820.5 JOD when the rates were added rather than compounded).',
    },
    // ── Inspection & appraisal ────────────────────────────────────────────────────────────────────
    {
        id: 'inspection',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Combined inspection deduction',
        unit: 'fraction',
        group: 'INSPECTION',
        expression: 'chassisRate + interiorRate + engineRate + hybridRate + tireRate',
        description: 'PP §7.1 InspectionRate. An unknown severity is never treated as Excellent; it leaves the valuation incomplete (PP AC03).',
    },
    {
        id: 'fixed',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Fixed damage deduction',
        unit: 'JOD',
        group: 'INSPECTION',
        expression: 'damageGroup1 + damageGroup2',
        description: 'One priced selection per Arabic damage group (PP §7.3). Further findings are recorded as evidence or as current work, never added here.',
    },
    {
        id: 'inspected',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Value after inspection',
        unit: 'JOD',
        group: 'INSPECTION',
        expression: 'base * (1 - inspection) - fixed',
        description: 'PP §7.1 InspectedValue. A negative inspected value blocks the valuation rather than being clipped to zero (PP §7.5).',
    },
    {
        id: 'afterFace',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Value after facelift',
        unit: 'JOD',
        group: 'INSPECTION',
        expression: 'inspected * (1 - faceliftRate)',
        description: 'PP §7.1. The rate is zero when facelift does not apply to this vehicle; unknown is not No.',
    },
    {
        id: 'afterCustoms',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Value after customs impact',
        unit: 'JOD',
        group: 'INSPECTION',
        expression: 'afterFace * (1 - customsImpactRate)',
        description: 'PP §7.1. Customs here is a market-value adjustment; an actual tax or traffic charge is its own cost line.',
    },
    {
        id: 'inclusion',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Add-ons included with the vehicle',
        unit: 'JOD',
        group: 'INSPECTION',
        expression: 'spareKeyValue + ownersManualValue + toolKitValue + spareTireValue',
        description: 'PP §7.1 approvedInclusions: the spare key, owner’s manual, tool kit and spare tyre that came with the vehicle.',
    },
    {
        id: 'appraisal',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Rounded model appraisal',
        unit: 'JOD',
        group: 'INSPECTION',
        expression: 'round((afterCustoms + inclusion) / roundingIncrement) * roundingIncrement',
        description: 'PP §7.1 ModelAppraisal: the appraisal rounds to the nearest commercial increment, while ceilings round down and retail rounds up.',
    },
    // ── Fees & total cost ─────────────────────────────────────────────────────────────────────────
    {
        id: 'remaining',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Registration remaining days',
        unit: 'days',
        group: 'COSTS',
        expression: 'max(0, registrationDaysDifference)',
        description: 'PP §7.2 RegistrationRemainingDays: calendar days from the valuation date to registration expiry, never negative.',
    },
    {
        id: 'licensing',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Licensing estimate',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'LICENSING',
        expression: 'if(remaining < registrationThresholdDays, inspected * licensingRate, 0)',
        description: 'PP §7.2 LicensingEstimate: licensing is only expected when little registration time remains.',
    },
    {
        id: 'general',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'General fees',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'GENERAL_FEES',
        expression: 'if(remaining < registrationThresholdDays, generalFee, 0)',
        description: 'PP §7.2 GeneralFeesEstimate.',
    },
    {
        id: 'registration',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Registration fee',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'REGISTRATION',
        expression: 'if(remaining < registrationThresholdDays, registrationFee, 0)',
        description: 'PP §7.2 RegistrationEstimate. The fee itself lives in the template version, not in this expression (PP AC21).',
    },
    {
        id: 'transfer',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Ownership transfer fee',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'TRANSFER',
        expression: 'inspected * ownershipTransferRate',
        description: 'PP §7.2 TransferEstimate.',
    },
    {
        id: 'insurance',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Vehicle insurance',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'INSURANCE',
        expression: 'min(insuranceCap, inspected * insuranceRate)',
        description: 'PP §7.2 InsuranceEstimate. Two templates share this expression and differ only in their cap (PP AC20).',
    },
    {
        id: 'commission',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Sales commission',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'COMMISSION',
        expression: 'commissionFee',
        description: 'PP §7.3: a flat sales commission, overridable per vehicle.',
    },
    {
        id: 'fuel',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Fuel cost',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'FUEL',
        expression: 'fuelLiters * fuelPricePerLiter',
        description: 'PP §7.2 FuelEstimate.',
    },
    {
        id: 'overhead',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Overhead',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'OVERHEAD',
        expression: 'inspected * overheadRate',
        description: 'PP §7.2 OverheadEstimate on the source-compatible base. The base awaits Finance approval.',
    },
    {
        id: 'holding',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Holding cost',
        unit: 'JOD',
        group: 'COSTS',
        expression: 'if(isSold, daysInStock, max(expectedHoldingDays, daysInStock)) * dailyHoldingRate',
        description: 'PP §7.2 HoldingEstimate, extended to real stock age: while the vehicle is unsold it is the greater of the expectation and the days it has actually stood; once sold it is the days it stood. Holding is accounted once and is never a cost item (plan §5.4).',
    },
    {
        id: 'repair',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Current reconditioning',
        unit: 'JOD',
        group: 'COSTS',
        expression: 'reconditioningResolved',
        description: 'PP §7.2 ReconditioningEstimate, read from the resolved cost items: an actual invoice replaces its estimate, a part-finished job is actual-to-date plus remaining, and a missing actual is never zero.',
    },
    {
        id: 'taxTraffic',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Tax and traffic charges',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'TAX_TRAFFIC',
        expression: 'taxTrafficEstimate',
        description: 'PP §7.2 actualTaxTrafficEstimate, kept apart from the customs market adjustment in the appraisal.',
    },
    {
        id: 'otherDirect',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Other direct costs',
        unit: 'JOD',
        group: 'COSTS',
        costCategory: 'OTHER',
        expression: 'otherDirectCostEstimate',
        description: 'PP §7.2 otherDirectCostEstimate.',
    },
    {
        id: 'costs',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Costs CTA expects to pay after buying',
        unit: 'JOD',
        group: 'COSTS',
        expression: 'licensing + general + registration + transfer + insurance + commission + fuel + overhead + holding + repair + taxTraffic + otherDirect',
        description: 'PP §7.2 ExpectedNonAcquisitionCosts. Every component is counted exactly once, here and nowhere else. They are part of the projected total cost and the required retail, but they no longer reduce the acquisition ceiling (business review 2026-09-23).',
    },
    // ── Acquisition & retail ──────────────────────────────────────────────────────────────────────
    {
        id: 'profitAllowance',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Profit CTA wants to make',
        unit: 'JOD',
        group: 'ACQUISITION',
        expression: 'if(useGrossMargin, appraisal * targetMargin, targetProfit)',
        description: 'PP §7.2: a valuation prices on a fixed profit or on a gross margin, never on both.',
    },
    {
        id: 'ceiling',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Most CTA may pay (calculated price)',
        unit: 'JOD',
        group: 'ACQUISITION',
        expression: SEED_CEILING_EXPRESSION,
        description: 'The acquisition ceiling: the appraisal less the profit CTA wants and the safety margin, rounded down to the commercial increment. Expected costs after buying are NOT deducted (business review 2026-09-23); they still count in the projected total cost and the required retail. A negative ceiling is a valid "no economic offer" finding, not an error (PP §7.5).',
    },
    {
        id: 'total',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Everything CTA will have spent (price + costs)',
        unit: 'JOD',
        group: 'ACQUISITION',
        expression: 'acquisitionBasis + costs',
        description: 'PP §7.2 ProjectedTotalCost. Before a purchase the basis is the proposed offer and the result is labelled projected; afterwards the actual purchase price is authoritative.',
    },
    {
        id: 'retail',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Price CTA must sell at',
        unit: 'JOD',
        group: 'ACQUISITION',
        expression: 'ceil(if(useGrossMargin, total / (1 - targetMargin), total + targetProfit) / roundingIncrement) * roundingIncrement',
        description: 'PP §7.2 RequiredRetail, rounded up to the commercial increment.',
    },
    {
        id: 'profit',
        kind: 'SYSTEM',
        scope: 'ALWAYS',
        label: 'Profit at model appraisal',
        unit: 'JOD',
        group: 'ACQUISITION',
        expression: 'appraisal - total',
        description: 'PP §7.2 ExpectedProfitAtModelValue: what the vehicle would earn if it sold at its own appraisal.',
    },
    // ── Trade-in ──────────────────────────────────────────────────────────────────────────────────
    {
        id: 'newMarginFactor',
        kind: 'SYSTEM',
        scope: 'TRADE_IN',
        label: 'New-car margin band incentive',
        unit: 'fraction',
        group: 'TRADE_IN',
        expression: 'if(newVehicleMargin < 0.05, 0, if(newVehicleMargin < 0.08, 0.05, if(newVehicleMargin < 0.12, 0.10, if(newVehicleMargin < 0.18, 0.20, 0.30))))',
        description: 'PP §7.4 NewMarginFactor: the banded incentive on the new vehicle’s margin.',
    },
    {
        id: 'supportRate',
        kind: 'SYSTEM',
        scope: 'TRADE_IN',
        label: 'Trade-in support rate',
        unit: 'fraction',
        group: 'TRADE_IN',
        expression: 'demandFactor + stockFactor + newMarginFactor',
        description: 'PP §7.4 SupportRate: demand plus stock plus the new-car margin band.',
    },
    {
        id: 'support',
        kind: 'SYSTEM',
        scope: 'TRADE_IN',
        label: 'Approved support calculation',
        unit: 'JOD',
        group: 'TRADE_IN',
        expression: 'min(newVehicleProfit * supportRate, supportCap)',
        description: 'PP §7.4 ApprovedSupport. In MVP this is a scenario figure only — the maximum trade-in offer is the ceiling plus this support — and it never enters the total cost, the required retail or the dashboard (plan §5.9).',
    },
];
/** A fresh copy, so a caller that mutates a draft cannot mutate the seed. */
export const seedPolicyV1 = () => SEED_POLICY_V1.map((equation) => ({ ...equation }));
//# sourceMappingURL=seed-policy-v1.js.map